import React from 'react';
import { useIntl } from 'react-intl';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import {
  IconBox,
  IconCalendar,
  IconChevronLeft,
  IconOrders,
  IconRefresh,
  IconTick,
} from '../../assets/svg/icons';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import ScCard from '../../components/atoms/ScCard';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import { OrderView, OrderStage } from './types';
import { iqd } from './format';
import { CartButton } from './components';
import {
  StyledBackChip,
  StyledBreadcrumb,
  StyledEmpty,
  StyledErrorState,
  StyledSkeletonBlock,
  StyledSkeletonCard,
} from './styles';
import {
  StyledOrderCard,
  StyledOrderMeta,
  StyledOrderMetaItem,
  StyledOrdersWrap,
  StyledReceiptCode,
  StyledReceiptHeader,
  StyledStageLine,
  StyledStageLabel,
  StyledStageLabels,
  StyledStageNode,
  StyledStageTrack,
} from './styles2';

// ── Stage constants ──────────────────────────────────────────────────────────

const STAGES: OrderStage[] = ['New', 'Picking', 'Packing', 'Shipped', 'Delivered'];

// EN label per stage.
const STAGE_LABEL_EN: Record<OrderStage, string> = {
  New: 'Confirmed', Picking: 'Being prepared', Packing: 'Being packed',
  Shipped: 'On the way', Delivered: 'Delivered',
};

// AR label per stage.
const STAGE_LABEL_AR: Record<OrderStage, string> = {
  New: 'مؤكَّد', Picking: 'قيد التجهيز', Packing: 'قيد التغليف',
  Shipped: 'في الطريق', Delivered: 'تم التوصيل',
};

// Badge tone progression: neutral → warning → warning → info(neutral-ink) → success.
const STAGE_TONE: Record<OrderStage, BadgeTone> = {
  New: BadgeTone.Neutral,
  Picking: BadgeTone.Warning,
  Packing: BadgeTone.Warning,
  Shipped: BadgeTone.Neutral,
  Delivered: BadgeTone.Success,
};

const normalizeStage = (s: string): OrderStage =>
  (STAGES.includes(s as OrderStage) ? s : 'New') as OrderStage;

// ── Stage progress bar ───────────────────────────────────────────────────────

const StageProgress: React.FC<{ stage: OrderStage; isAr: boolean }> = ({ stage, isAr }) => {
  const idx = STAGES.indexOf(stage);
  return (
    <>
      <StyledStageTrack role="progressbar" aria-label={isAr ? STAGE_LABEL_AR[stage] : STAGE_LABEL_EN[stage]} aria-valuenow={idx + 1} aria-valuemin={1} aria-valuemax={STAGES.length}>
        {STAGES.map((s, i) => {
          const done = i < idx;
          const current = i === idx;
          return (
            <React.Fragment key={s}>
              <StyledStageNode $done={done} $current={current}>
                {done && <IconTick />}
              </StyledStageNode>
              {i < STAGES.length - 1 && <StyledStageLine $done={i < idx} />}
            </React.Fragment>
          );
        })}
      </StyledStageTrack>
      <StyledStageLabels aria-hidden="true">
        {STAGES.map((s, i) => {
          const done = i < idx;
          const current = i === idx;
          const label = isAr ? STAGE_LABEL_AR[s] : STAGE_LABEL_EN[s];
          return (
            <StyledStageLabel key={s} $done={done} $current={current}>{label}</StyledStageLabel>
          );
        })}
      </StyledStageLabels>
    </>
  );
};

// ── Date formatting ──────────────────────────────────────────────────────────

const formatDate = (iso: string | undefined, locale: string): string => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// ── Order card ───────────────────────────────────────────────────────────────

interface OrderCardProps {
  order: OrderView;
  isAr: boolean;
  cur: string;
  intl: ReturnType<typeof useIntl>;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, isAr, cur, intl }) => {
  const stage = normalizeStage(order.stage);
  const tone = STAGE_TONE[stage];
  const stageLabel = isAr ? STAGE_LABEL_AR[stage] : STAGE_LABEL_EN[stage];
  const itemCount = order.itemCount ?? order.items.length;
  const dateLocale = isAr ? 'ar-IQ' : 'en-US';

  return (
    <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
      <StyledOrderCard>
        <StyledReceiptHeader>
          <div>
            <div className="eyebrow">
              {order.merchantName
                ? intl.formatMessage({ id: 'marketplace.purchaseOrder', defaultMessage: 'Purchase order' }) + ' · ' + order.merchantName
                : intl.formatMessage({ id: 'marketplace.purchaseOrder', defaultMessage: 'Purchase order' })}
            </div>
            <StyledReceiptCode className="mono tnum">{order.code}</StyledReceiptCode>
          </div>
          <Badge tone={tone} dot={stage === 'Delivered'}>{stageLabel}</Badge>
        </StyledReceiptHeader>

        <StageProgress stage={stage} isAr={isAr} />

        <StyledOrderMeta>
          <StyledOrderMetaItem>
            <IconBox />
            <span>
              {intl.formatMessage(
                { id: 'marketplace.orders.itemCount', defaultMessage: '{n, plural, one {# item} other {# items}}' },
                { n: itemCount }
              )}
            </span>
          </StyledOrderMetaItem>
          <StyledOrderMetaItem>
            <IconCalendar />
            <span>{formatDate(order.createdAt, dateLocale)}</span>
          </StyledOrderMetaItem>
          <StyledOrderMetaItem style={{ marginInlineStart: 'auto' }}>
            <span className="mono tnum" style={{ fontWeight: 700, fontSize: 'var(--t-base)', color: 'var(--ink)' }}>
              {iqd(order.totalIqd)}
            </span>
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{cur}</span>
          </StyledOrderMetaItem>
        </StyledOrderMeta>
      </StyledOrderCard>
    </ScCard>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────

interface ScreenOrdersProps {
  orders: OrderView[];
  isLoading: boolean;
  isError: boolean;
  cartCount: number;
  onBack: () => void;
  onOpenCart: () => void;
  onRetry: () => void;
}

const ScreenOrders: React.FC<ScreenOrdersProps> = ({
  orders,
  isLoading,
  isError,
  cartCount,
  onBack,
  onOpenCart,
  onRetry,
}) => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const isAr = language === SupportedLanguages.Arabic;
  const cur = intl.formatMessage({ id: 'marketplace.currency', defaultMessage: 'IQD' });

  return (
    <>
      <StyledBreadcrumb>
        <StyledBackChip type="button" onClick={onBack}>
          <IconChevronLeft />
          <span>{intl.formatMessage({ id: 'mainNavigation.marketplace', defaultMessage: 'Marketplace' })}</span>
        </StyledBackChip>
      </StyledBreadcrumb>

      <ScPageHeader
        eyebrow={intl.formatMessage({ id: 'marketplace.eyebrow', defaultMessage: 'Wholesale sourcing' })}
        title={intl.formatMessage({ id: 'marketplace.orders.title', defaultMessage: 'My Orders' })}
        subtitle={
          isLoading || isError
            ? undefined
            : orders.length > 0
            ? intl.formatMessage(
                { id: 'marketplace.orders.subtitle', defaultMessage: '{n, plural, one {# purchase order} other {# purchase orders}}' },
                { n: orders.length }
              )
            : intl.formatMessage({ id: 'marketplace.orders.subtitleEmpty', defaultMessage: 'No orders yet' })
        }
        actions={
          <CartButton
            count={cartCount}
            onClick={onOpenCart}
            label={intl.formatMessage({ id: 'marketplace.cart.label', defaultMessage: 'Cart' })}
          />
        }
      />

      {isLoading ? (
        <StyledOrdersWrap>
          {[0, 1, 2].map((i) => (
            <StyledSkeletonCard key={i} aria-hidden="true">
              <StyledSkeletonBlock $h="1rem" $w="40%" />
              <StyledSkeletonBlock $h="1.5rem" $w="55%" />
              <StyledSkeletonBlock $h="1.25rem" />
              <StyledSkeletonBlock $h="0.75rem" $w="70%" />
            </StyledSkeletonCard>
          ))}
        </StyledOrdersWrap>
      ) : isError ? (
        <StyledErrorState role="alert">
          <IconRefresh style={{ width: 32, height: 32, opacity: 0.4 }} aria-hidden="true" />
          <span>{intl.formatMessage({ id: 'marketplace.orders.loadFailed', defaultMessage: "Couldn't load your orders. Please try again." })}</span>
          <ScButton kind={ButtonKind.Outline} onClick={onRetry}>
            {intl.formatMessage({ id: 'marketplace.retry', defaultMessage: 'Retry' })}
          </ScButton>
        </StyledErrorState>
      ) : orders.length === 0 ? (
        <ScCard pad={false}>
          <StyledEmpty style={{ padding: 'var(--s-10) var(--s-4)' }}>
            <IconOrders style={{ width: 40, height: 40, opacity: 0.25 }} aria-hidden="true" />
            <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)', color: 'var(--ink)' }}>
              {isAr ? 'لا توجد طلبات بعد' : 'No orders yet'}
            </div>
            <div className="muted" style={{ fontSize: 'var(--t-sm)', maxWidth: 340, textAlign: 'center' }}>
              {isAr
                ? 'بعد تأكيد مشتريات السوق ستظهر هنا لمتابعة مراحل الشحن.'
                : 'Once you place marketplace orders they appear here for tracking.'}
            </div>
            <ScButton kind={ButtonKind.Primary} size={ButtonSize.Large} icon={IconBox} onClick={onBack}>
              {intl.formatMessage({ id: 'marketplace.browse', defaultMessage: 'Browse marketplace' })}
            </ScButton>
          </StyledEmpty>
        </ScCard>
      ) : (
        <StyledOrdersWrap>
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} isAr={isAr} cur={cur} intl={intl} />
          ))}
        </StyledOrdersWrap>
      )}
    </>
  );
};

export { STAGE_LABEL_EN, STAGE_LABEL_AR, STAGES };
export default ScreenOrders;
